// src/settings/GroupsPage.jsx

import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  IconButton,
  Tooltip,
  TableFooter,
  TablePagination,
  Paper,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PublishIcon from '@mui/icons-material/Publish';
import GroupsIcon from '@mui/icons-material/Groups';

import { useEffectAsync } from '../reactHelper';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionFab from './components/CollectionFab';
import CollectionActions from './components/CollectionActions';
import TableShimmer from '../common/components/TableShimmer';
import SearchHeader from './components/SearchHeader';
import {
  useRestriction,
  useAdministrator,
  useDeviceReadonly,
} from '../common/util/permissions';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_ROWS_PER_PAGE = 50;
const SEARCH_DEBOUNCE = 300;

const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

const buildSearchIndex = (group) => [
  group.name,
  group.description,
].filter(Boolean).join(' ').toLowerCase();

const matchesKeyword = (indexedText, keyword) => {
  if (!keyword) return true;

  const tokens = keyword
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (!tokens.length) return true;

  return tokens.every((token) => indexedText.includes(token));
};

const GroupRow = memo(({
  item,
  classes,
  renderActions,
}) => (
  <TableRow hover>
    <TableCell>{item.name}</TableCell>
    <TableCell className={classes.columnAction} padding="none">
      {renderActions(item.id)}
    </TableCell>
  </TableRow>
));

GroupRow.displayName = 'GroupRow';

const GroupsKpi = memo(({ totalGroups, filteredGroups, hasActiveFilter, t }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'action.hover',
        }}
      >
        <GroupsIcon fontSize="medium" />
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('settingsGroups') || 'Grupos'}
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {totalGroups.toLocaleString('pt-BR')}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {hasActiveFilter
            ? `${filteredGroups.toLocaleString('pt-BR')} exibido(s) no filtro`
            : 'Total de grupos cadastrados'}
        </Typography>
      </Box>
    </Box>
  </Paper>
));

GroupsKpi.displayName = 'GroupsKpi';

const GroupsPage = () => {
  const { classes } = useSettingsStyles();
  const navigate = useNavigate();
  const t = useTranslation();

  const limitCommands = useRestriction('limitCommands');
  const admin = useAdministrator();
  const deviceReadonly = useDeviceReadonly();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedKeyword(normalizeText(searchKeyword));
      setPage(0);
    }, SEARCH_DEBOUNCE);

    return () => window.clearTimeout(timeout);
  }, [searchKeyword]);

  const actionCommand = useMemo(() => ({
    key: 'command',
    title: t('deviceCommand'),
    icon: <PublishIcon fontSize="small" />,
    handler: (groupId) => navigate(`/settings/group/${groupId}/command`),
  }), [navigate, t]);

  const actionConnections = useMemo(() => ({
    key: 'connections',
    title: t('sharedConnections'),
    icon: <LinkIcon fontSize="small" />,
    handler: (groupId) => navigate(`/settings/group/${groupId}/connections`),
  }), [navigate, t]);

  const permissionMatrix = useMemo(() => {
    const commandAllowed = !limitCommands;
    const baseActions = [actionConnections];

    const buildActions = () => (
      commandAllowed ? [...baseActions, actionCommand] : baseActions
    );

    if (admin) {
      return {
        canEditGroup: true,
        canRemoveGroup: true,
        allowedActions: buildActions(),
      };
    }

    if (deviceReadonly) {
      return {
        canEditGroup: false,
        canRemoveGroup: false,
        allowedActions: buildActions(),
      };
    }

    return {
      canEditGroup: false,
      canRemoveGroup: false,
      allowedActions: buildActions(),
    };
  }, [admin, deviceReadonly, limitCommands, actionConnections, actionCommand]);

  const { canEditGroup, canRemoveGroup, allowedActions } = permissionMatrix;

  useEffectAsync(async () => {
    const controller = new AbortController();
    setLoading(true);
    setErrorText('');

    try {
      const url = admin ? '/api/groups?all=true' : '/api/groups';

      const response = await fetchOrThrow(url, {
        signal: controller.signal,
      });

      const data = await response.json();

      const indexedGroups = (Array.isArray(data) ? data : []).map((group) => ({
        ...group,
        _searchIndex: buildSearchIndex(group),
      }));

      setItems(indexedGroups);
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setErrorText(e?.message || 'Erro ao carregar grupos');
        setItems([]);
      }
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, [timestamp, admin]);

  const filteredItems = useMemo(() => (
    items.filter((item) => matchesKeyword(item._searchIndex, debouncedKeyword))
  ), [items, debouncedKeyword]);

  const totalCount = filteredItems.length;
  const totalGroups = items.length;
  const hasActiveFilter = Boolean(debouncedKeyword);

  const safePage = useMemo(() => {
    if (totalCount === 0) {
      return 0;
    }

    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    return Math.min(page, maxPage);
  }, [page, rowsPerPage, totalCount]);

  const paginatedItems = useMemo(() => {
    const startIndex = safePage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, safePage, rowsPerPage]);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReload = () => {
    setTimestamp(Date.now());
  };

  const renderActions = useCallback((groupId) => {
    if (canEditGroup || canRemoveGroup) {
      return (
        <CollectionActions
          itemId={groupId}
          editPath="/settings/group"
          endpoint="groups"
          setTimestamp={handleReload}
          customActions={allowedActions}
          readonly={!canEditGroup}
          disableRemove={!canRemoveGroup}
        />
      );
    }

    return (
      <>
        {allowedActions.map((action) => (
          <Tooltip key={action.key} title={action.title}>
            <IconButton size="small" onClick={() => action.handler(groupId)}>
              {action.icon}
            </IconButton>
          </Tooltip>
        ))}
      </>
    );
  }, [allowedActions, canEditGroup, canRemoveGroup]);

  return (
    <PageLayout
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'settingsGroups']}
    >
      {!loading && !errorText && (
        <GroupsKpi
          totalGroups={totalGroups}
          filteredGroups={totalCount}
          hasActiveFilter={hasActiveFilter}
          t={t}
        />
      )}

      <SearchHeader
        keyword={searchKeyword}
        setKeyword={setSearchKeyword}
      />

      {errorText ? (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {errorText}
        </Alert>
      ) : null}

      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>{t('sharedName')}</TableCell>
            <TableCell className={classes.columnAction} />
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableShimmer columns={2} endAction />
          ) : (
            paginatedItems.map((item) => (
              <GroupRow
                key={item.id}
                item={item}
                classes={classes}
                renderActions={renderActions}
              />
            ))
          )}

          {!loading && !errorText && paginatedItems.length === 0 && (
            <TableRow>
              <TableCell colSpan={2} align="center">
                {t('sharedNoData')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TableCell colSpan={2}>
              {!loading && !errorText && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={safePage}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  labelRowsPerPage="Itens por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
              )}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      {admin && <CollectionFab editPath="/settings/group" />}
    </PageLayout>
  );
};

export default GroupsPage;
